import React, { useState, useEffect } from 'react';
import { investmentAPI } from '../../services/api';
import { Plus, Edit, Trash2, Save, X, FileDown, Settings, Eye, Upload } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import ExcelExportModal from '../ExcelExportModal';
import ColumnVisibilityModal from '../ColumnVisibilityModal';
import DetailViewModal from '../DetailViewModal';
import BulkUploadModal from '../BulkUploadModal';

const FMVTab = ({ investmentId, currency, investment }) => {
  const { user } = useAuth();
  const { canDelete = false, canEdit = false, canCreate = false, canDownload = false } = usePermissions() || {};
  const [fmvRecords, setFmvRecords] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showCustomFieldsManager, setShowCustomFieldsManager] = useState(false);
  const [showColumnVisibility, setShowColumnVisibility] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [selectedFMV, setSelectedFMV] = useState(null);
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [formData, setFormData] = useState({
    date_of_fmv: '',
    type_of_valuation: 'Internal Valuation',
    company_valuation: '',
    value_of_fund_stake: '',
    custom_fields: {}
  });

  const [newCustomField, setNewCustomField] = useState({
    name: '',
    type: 'text',
    options: []
  });

  useEffect(() => {
    fetchFMV();
    const fields = loadCustomFieldDefinitions();
    loadColumnVisibility(fields);
  }, [investmentId]);

  const loadCustomFieldDefinitions = () => {
    const saved = localStorage.getItem(`custom_fields_fmv_${investmentId}`);
    if (saved) {
      const fields = JSON.parse(saved);
      setCustomFieldDefinitions(fields);
      return fields;
    }
    return [];
  };

  const loadColumnVisibility = (customFields = []) => {
    const saved = localStorage.getItem(`column_visibility_fmv_${investmentId}`);
    if (saved) {
      const savedVisibility = JSON.parse(saved);
      // Ensure all custom fields have visibility state (default to true if not present)
      const updatedVisibility = { ...savedVisibility };
      customFields.forEach(field => {
        const key = `custom_${field.id}`;
        if (!(key in updatedVisibility)) {
          updatedVisibility[key] = true;
        }
      });
      setColumnVisibility(updatedVisibility);
    } else {
      // Initialize with all columns visible
      const defaultVisibility = {
        date_of_fmv: true,
        type_of_valuation: true,
        company_valuation: true,
        value_of_fund_stake: true
      };
      // Add custom fields visibility
      customFields.forEach(field => {
        defaultVisibility[`custom_${field.id}`] = true;
      });
      setColumnVisibility(defaultVisibility);
    }
  };

  const saveColumnVisibility = (visibility) => {
    localStorage.setItem(`column_visibility_fmv_${investmentId}`, JSON.stringify(visibility));
    setColumnVisibility(visibility);
  };

  const handleToggleColumn = (columnKey) => {
    const updated = { ...columnVisibility, [columnKey]: !columnVisibility[columnKey] };
    saveColumnVisibility(updated);
  };

  const handleSelectAllColumns = () => {
    const allColumns = getAllColumns().reduce((acc, col) => {
      acc[col.key] = true;
      return acc;
    }, {});
    saveColumnVisibility(allColumns);
  };

  const handleDeselectAllColumns = () => {
    const allColumns = getAllColumns().reduce((acc, col) => {
      acc[col.key] = false;
      return acc;
    }, {});
    saveColumnVisibility(allColumns);
  };

  const getAllColumns = () => {
    const baseColumns = [
      { key: 'date_of_fmv', label: 'Date of FMV', required: true },
      { key: 'type_of_valuation', label: 'Valuation Type', required: true },
      { key: 'company_valuation', label: `Company Valuation (${currency})`, required: false },
      { key: 'value_of_fund_stake', label: `Fund Stake Value (${currency})`, required: false }
    ];

    // Add custom field columns
    const customColumns = customFieldDefinitions.map(field => ({
      key: `custom_${field.id}`,
      label: `Custom: ${field.name}`,
      required: false
    }));

    return [...baseColumns, ...customColumns];
  };

  const isColumnVisible = (columnKey) => {
    return columnVisibility[columnKey] !== false;
  };

  const handleRowClick = (record) => {
    setSelectedFMV(record);
    setShowDetailModal(true);
  };

  const saveCustomFieldDefinitions = (definitions) => {
    localStorage.setItem(`custom_fields_fmv_${investmentId}`, JSON.stringify(definitions));
    setCustomFieldDefinitions(definitions);
    
    // Update column visibility to include new custom fields (default visible)
    const updatedVisibility = { ...columnVisibility };
    definitions.forEach(field => {
      const key = `custom_${field.id}`;
      if (!(key in updatedVisibility)) {
        updatedVisibility[key] = true;
      }
    });
    setColumnVisibility(updatedVisibility);
    localStorage.setItem(`column_visibility_fmv_${investmentId}`, JSON.stringify(updatedVisibility));
  };

  const fetchFMV = async () => {
    try {
      const response = await investmentAPI.getFMVRecords(investmentId);
      setFmvRecords(response.data);
    } catch (error) {
      console.error('Failed to fetch FMV:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Prepare data - convert string values to numbers
      const dataToSend = {
        ...formData,
        company_valuation: parseFloat(formData.company_valuation),
        value_of_fund_stake: parseFloat(formData.value_of_fund_stake)
      };
      
      if (editingId) {
        await investmentAPI.updateFMV(investmentId, editingId, dataToSend);
      } else {
        await investmentAPI.createFMV(investmentId, dataToSend);
      }
      fetchFMV();
      resetForm();
    } catch (error) {
      console.error('FMV save error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to save FMV record';
      alert(errorMsg);
    }
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    setFormData({
      date_of_fmv: record.date_of_fmv,
      type_of_valuation: record.type_of_valuation,
      company_valuation: record.company_valuation,
      value_of_fund_stake: record.value_of_fund_stake,
      custom_fields: record.custom_fields || {}
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this FMV record?')) return;
    try {
      await investmentAPI.deleteFMV(investmentId, id);
      fetchFMV();
    } catch (error) {
      alert('Failed to delete FMV record');
    }
  };

  const resetForm = () => {
    setFormData({
      date_of_fmv: '',
      type_of_valuation: 'Internal Valuation',
      company_valuation: '',
      value_of_fund_stake: '',
      custom_fields: {}
    });
    setShowForm(false);
    setEditingId(null);
  };

  const handleCustomFieldChange = (fieldName, value) => {
    setFormData({
      ...formData,
      custom_fields: {
        ...formData.custom_fields,
        [fieldName]: value
      }
    });
  };

  const addCustomField = () => {
    if (!newCustomField.name.trim()) {
      alert('Field name is required');
      return;
    }

    const field = {
      id: Date.now().toString(),
      name: newCustomField.name.trim(),
      type: newCustomField.type,
      options: newCustomField.options
    };

    const updated = [...customFieldDefinitions, field];
    saveCustomFieldDefinitions(updated);
    setNewCustomField({ name: '', type: 'text', options: [] });
  };

  const removeCustomField = (fieldId) => {
    if (!window.confirm('Remove this custom field? Data will be lost.')) return;
    const updated = customFieldDefinitions.filter(f => f.id !== fieldId);
    saveCustomFieldDefinitions(updated);
  };

  const renderCustomField = (field) => {
    const value = formData.custom_fields[field.name] || '';

    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
            className="w-full px-4 py-2 border rounded-lg"
          />
        );
      case 'number':
        return (
          <input
            type="number"
            step="0.01"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
            className="w-full px-4 py-2 border rounded-lg"
          />
        );
      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
            className="w-full px-4 py-2 border rounded-lg"
          />
        );
      case 'dropdown':
        return (
          <select
            value={value}
            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
            className="w-full px-4 py-2 border rounded-lg"
          >
            <option value="">Select...</option>
            {field.options.map((opt, idx) => (
              <option key={idx} value={opt}>{opt}</option>
            ))}
          </select>
        );
      case 'radio':
        return (
          <div className="space-y-2">
            {field.options.map((opt, idx) => (
              <label key={idx} className="flex items-center">
                <input
                  type="radio"
                  name={field.name}
                  value={opt}
                  checked={value === opt}
                  onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
                  className="mr-2"
                />
                {opt}
              </label>
            ))}
          </div>
        );
      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
            className="w-full px-4 py-2 border rounded-lg"
            rows="4"
          />
        );
      default:
        return null;
    }
  };

  // Bulk upload functions
  const getBulkUploadTemplate = () => {
    const template = {
      'Date *': '2024-06-15',
      'Valuation Type *': 'Internal Valuation',
      'Company Valuation *': '8000000',
      'Fund Stake Value *': '4000000'
    };

    // Add custom fields to template
    customFieldDefinitions.forEach(field => {
      template[field.name] = field.type === 'dropdown' || field.type === 'radio' 
        ? (field.options[0] || 'Sample Value')
        : 'Sample Value';
    });

    return template;
  };

  const formatDateForAPI = (dateValue) => {
    if (!dateValue) return null;
    
    // If it's already a string in YYYY-MM-DD format
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }
    
    // Handle Excel serial date numbers (days since 1899-12-30)
    if (typeof dateValue === 'number') {
      // Excel serial date: number of days since December 30, 1899
      const excelEpoch = new Date(1899, 11, 30); // Dec 30, 1899
      const date = new Date(excelEpoch.getTime() + dateValue * 24 * 60 * 60 * 1000);
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    // If it's a Date object or date string
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) {
      return null; // Invalid date
    }
    
    // Format as YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleBulkUpload = async (data) => {
    const results = {
      success: 0,
      failed: 0,
      errors: []
    };

    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      
      // Skip empty rows
      if (!row['Date *'] && !row['Date']) {
        continue;
      }
      
      try {
        // Format date properly
        const rawDate = row['Date *'] || row['Date'];
        const formattedDate = formatDateForAPI(rawDate);
        
        if (!formattedDate) {
          throw new Error('Invalid date format');
        }

        // Map CSV columns to API fields
        const fmvData = {
          date_of_fmv: formattedDate,
          type_of_valuation: row['Valuation Type *'] || row['Valuation Type'] || 'Internal Valuation',
          company_valuation: row['Company Valuation *'] || row['Company Valuation'] ? parseFloat(row['Company Valuation *'] || row['Company Valuation']) : undefined,
          value_of_fund_stake: row['Fund Stake Value *'] || row['Fund Stake Value'] ? parseFloat(row['Fund Stake Value *'] || row['Fund Stake Value']) : undefined,
          custom_fields: {}
        };

        // Extract custom fields
        customFieldDefinitions.forEach(field => {
          if (row[field.name]) {
            fmvData.custom_fields[field.name] = row[field.name];
          }
        });

        // Validate required fields
        if (!fmvData.company_valuation) {
          throw new Error('Company Valuation is required');
        }
        if (!fmvData.value_of_fund_stake) {
          throw new Error('Fund Stake Value is required');
        }

        // Create FMV record
        await investmentAPI.createFMV(investmentId, fmvData);
        results.success++;
      } catch (error) {
        results.failed++;
        const errorMsg = `Row ${i + 2}: ${error.response?.data?.detail || error.message || 'Failed to create'}`;
        results.errors.push(errorMsg);
      }
    }

    // Refresh the list
    await fetchFMV();
    
    return results;
  };

  // Excel export columns definition
  const getExportColumns = () => {
    const baseColumns = [
      { key: 'date_of_fmv', label: 'Date of FMV', format: (val) => val },
      { key: 'type_of_valuation', label: 'Valuation Type', format: (val) => val },
      { key: 'company_valuation', label: `Company Valuation (${currency})`, format: (val) => val?.toLocaleString() || '0' },
      { key: 'value_of_fund_stake', label: `Fund Stake Value (${currency})`, format: (val) => val?.toLocaleString() || '0' },
    ];

    // Add custom field columns
    const customColumns = customFieldDefinitions.map(field => ({
      key: `custom_fields.${field.name}`,
      label: field.name,
      format: (val, record) => record.custom_fields?.[field.name] || 'N/A'
    }));

    return [...baseColumns, ...customColumns];
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold">Fair Market Value (FMV)</h3>
        <div className="flex space-x-2">
          <button
            onClick={() => setShowColumnVisibility(true)}
            className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            title="Column Visibility"
          >
            <Eye size={18} className="mr-2" />
            Columns
          </button>
          {canDownload && (
            <button
              onClick={() => setShowExportModal(true)}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              title="Export to Excel (Admin and Fund Manager only)"
            >
              <FileDown size={18} className="mr-2" />
              Export
            </button>
          )}
          {canCreate && (
            <>
              <button
                onClick={() => setShowBulkUpload(true)}
                className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                title="Bulk Upload"
              >
                <Upload size={18} className="mr-2" />
                Bulk Upload
              </button>
              <button
                onClick={() => setShowCustomFieldsManager(!showCustomFieldsManager)}
                className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                title="Manage Custom Fields"
              >
                <Settings size={18} className="mr-2" />
                Custom Fields
              </button>
              {!showForm && (
                <button
                  onClick={() => setShowForm(true)}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Plus size={18} className="mr-2" />
                  Add FMV Record
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Custom Fields Manager */}
      {showCustomFieldsManager && (
        <div className="mb-6 p-6 bg-gray-50 rounded-lg border">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-semibold">Manage Custom Fields</h4>
            <button onClick={() => setShowCustomFieldsManager(false)} className="text-gray-500">
              <X size={20} />
            </button>
          </div>

          <div className="mb-4 p-4 bg-white rounded border">
            <h5 className="font-medium mb-3">Add New Custom Field</h5>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input
                type="text"
                placeholder="Field Name"
                value={newCustomField.name}
                onChange={(e) => setNewCustomField({ ...newCustomField, name: e.target.value })}
                className="px-3 py-2 border rounded"
              />
              <select
                value={newCustomField.type}
                onChange={(e) => setNewCustomField({ ...newCustomField, type: e.target.value, options: [] })}
                className="px-3 py-2 border rounded"
              >
                <option value="text">Text</option>
                <option value="number">Number</option>
                <option value="date">Date</option>
                <option value="dropdown">Dropdown</option>
                <option value="radio">Radio Button</option>
                <option value="textarea">Multi-line Text Box</option>
              </select>
              <button
                onClick={addCustomField}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Add Field
              </button>
            </div>

            {(newCustomField.type === 'dropdown' || newCustomField.type === 'radio') && (
              <div className="mt-3">
                <label className="block text-sm font-medium mb-2">Options (comma-separated):</label>
                <input
                  type="text"
                  placeholder="Option1, Option2, Option3"
                  onChange={(e) => setNewCustomField({
                    ...newCustomField,
                    options: e.target.value.split(',').map(o => o.trim()).filter(o => o)
                  })}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
            )}
          </div>

          <div>
            <h5 className="font-medium mb-2">Existing Custom Fields:</h5>
            {customFieldDefinitions.length === 0 ? (
              <p className="text-gray-500 text-sm">No custom fields defined yet</p>
            ) : (
              <div className="space-y-2">
                {customFieldDefinitions.map((field) => (
                  <div key={field.id} className="flex items-center justify-between p-3 bg-white border rounded">
                    <div>
                      <span className="font-medium">{field.name}</span>
                      <span className="ml-2 text-sm text-gray-600">({field.type})</span>
                      {field.options.length > 0 && (
                        <span className="ml-2 text-sm text-gray-500">
                          [{field.options.join(', ')}]
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => removeCustomField(field.id)}
                      className="text-red-600 hover:bg-red-50 p-1 rounded"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="mb-6 p-6 bg-gray-50 rounded-lg border">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-semibold">{editingId ? 'Edit FMV Record' : 'Add FMV Record'}</h4>
            <button type="button" onClick={resetForm} className="text-gray-500 hover:text-gray-700">
              <X size={20} />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-2">Date of FMV *</label>
              <input
                type="date"
                required
                value={formData.date_of_fmv}
                onChange={(e) => setFormData({ ...formData, date_of_fmv: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Type of Valuation *</label>
              <select
                required
                value={formData.type_of_valuation}
                onChange={(e) => setFormData({ ...formData, type_of_valuation: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg"
              >
                <option value="Internal Valuation">Internal Valuation</option>
                <option value="Independent Valuation">Independent Valuation</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Company Valuation ({currency}) *</label>
              <input
                type="number"
                required
                step="0.01"
                value={formData.company_valuation}
                onChange={(e) => setFormData({ ...formData, company_valuation: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Value of Fund Stake ({currency}) *</label>
              <input
                type="number"
                required
                step="0.01"
                value={formData.value_of_fund_stake}
                onChange={(e) => setFormData({ ...formData, value_of_fund_stake: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>

            {/* Custom Fields */}
            {customFieldDefinitions.length > 0 && customFieldDefinitions.map((field) => (
              <div key={field.id} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                <label className="block text-sm font-medium mb-2">{field.name}</label>
                {renderCustomField(field)}
              </div>
            ))}
          </div>

          <div className="flex justify-end space-x-2">
            <button type="button" onClick={resetForm} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              <Save size={18} className="mr-2" />
              {editingId ? 'Update' : 'Save'}
            </button>
          </div>
        </form>
      )}

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              {isColumnVisible('date_of_fmv') && (
                <th className="px-4 py-3 text-left text-sm font-medium">Date</th>
              )}
              {isColumnVisible('type_of_valuation') && (
                <th className="px-4 py-3 text-left text-sm font-medium">Valuation Type</th>
              )}
              {isColumnVisible('company_valuation') && (
                <th className="px-4 py-3 text-right text-sm font-medium">Company Valuation ({currency})</th>
              )}
              {isColumnVisible('value_of_fund_stake') && (
                <th className="px-4 py-3 text-right text-sm font-medium">Fund Stake Value ({currency})</th>
              )}
              {/* Custom Field Columns */}
              {customFieldDefinitions.map((field) => 
                isColumnVisible(`custom_${field.id}`) && (
                  <th key={field.id} className="px-4 py-3 text-left text-sm font-medium">{field.name}</th>
                )
              )}
              <th className="px-4 py-3 text-center text-sm font-medium">View</th>
              {(canEdit || canDelete) && <th className="px-4 py-3 text-right text-sm font-medium">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {fmvRecords.map((record) => (
              <tr key={record.id} className="border-b hover:bg-gray-50">
                {isColumnVisible('date_of_fmv') && (
                  <td className="px-4 py-3 text-sm">{record.date_of_fmv}</td>
                )}
                {isColumnVisible('type_of_valuation') && (
                  <td className="px-4 py-3 text-sm">{record.type_of_valuation}</td>
                )}
                {isColumnVisible('company_valuation') && (
                  <td className="px-4 py-3 text-sm text-right">{record.company_valuation?.toLocaleString()}</td>
                )}
                {isColumnVisible('value_of_fund_stake') && (
                  <td className="px-4 py-3 text-sm text-right">{record.value_of_fund_stake?.toLocaleString()}</td>
                )}
                {/* Custom Field Columns */}
                {customFieldDefinitions.map((field) => 
                  isColumnVisible(`custom_${field.id}`) && (
                    <td key={field.id} className="px-4 py-3 text-sm">
                      {record.custom_fields?.[field.name] || '-'}
                    </td>
                  )
                )}
                <td className="px-4 py-3 text-center">
                  <button
                    onClick={() => handleRowClick(record)}
                    className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    title="View Details"
                  >
                    Details
                  </button>
                </td>
                {(canEdit || canDelete) && (
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end space-x-2">
                      {canEdit && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(record);
                          }}
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                          title="Edit FMV record"
                        >
                          <Edit size={16} />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(record.id);
                          }}
                          className="p-1 text-red-600 hover:bg-red-50 rounded"
                          title="Delete FMV record (Admin only)"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {fmvRecords.length === 0 && <p className="text-center py-8 text-gray-500">No FMV records yet</p>}
      </div>

      <ColumnVisibilityModal
        isOpen={showColumnVisibility}
        onClose={() => setShowColumnVisibility(false)}
        columns={getAllColumns()}
        visibleColumns={columnVisibility}
        onToggleColumn={handleToggleColumn}
        onSelectAll={handleSelectAllColumns}
        onDeselectAll={handleDeselectAllColumns}
      />

      <DetailViewModal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title={`FMV Record - ${selectedFMV?.date_of_fmv || ''}`}
        data={selectedFMV}
        fields={[
          { key: 'date_of_fmv', label: 'Date of FMV', format: 'text' },
          { key: 'type_of_valuation', label: 'Valuation Type', format: 'text' },
          { key: 'company_valuation', label: `Company Valuation`, format: 'currency' },
          { key: 'value_of_fund_stake', label: `Fund Stake Value`, format: 'currency' }
        ]}
        customFields={customFieldDefinitions}
        onEdit={canEdit ? handleEdit : null}
        onDelete={canDelete ? handleDelete : null}
        canModify={canEdit}
        currency={currency}
      />

      <BulkUploadModal
        isOpen={showBulkUpload}
        onClose={() => setShowBulkUpload(false)}
        templateColumns={[]}
        templateData={getBulkUploadTemplate()}
        onUpload={handleBulkUpload}
        entityName="FMV Records"
        filename={`${investment?.portfolio_company_name || 'Investment'}_FMV`}
      />

      <ExcelExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        data={fmvRecords}
        columns={getExportColumns()}
        filename={`${investment?.portfolio_company_name || 'Investment'}_FMV`}
      />
    </div>
  );
};

export default FMVTab;
